<template>
  <div class="product-page animate__animated animate__fadeIn">
    <Head>
      <!-- Primary Meta Tags -->
      <title>VTStack - {{ currentProduct.Name }}</title>
      <meta name="title" :content="'VTStack - '+ currentProduct.Name">
      <meta name="description"
            :content="currentProduct.Subtitle ?? currentProduct.Name">

      <!-- Open Graph / Facebook -->
      <meta property="og:type" content="website">
      <meta property="og:url" :content="appUrl + $route.path">
      <meta property="og:title" :content="'VTStack - '+ currentProduct.Name">
      <meta property="og:description"
            :content="currentProduct.Subtitle ?? currentProduct.Name">
      <meta property="og:image" :content="mediaElements.length > 0 &&
      mediaElements[currentMedia].type === 'audio' ?
      '/VTStack_partage_sound_assets.jpg':
      currentProduct.Previews > 0 ?
      currentProduct.Previews[0] : '/VTStack_partage_graph_assets.jpg'">

      <!-- Twitter -->
      <meta property="twitter:card" content="summary_large_image">
      <meta property="twitter:url" :content="appUrl + $route.path">
      <meta property="twitter:title" :content="'VTStack - '+ currentProduct.Name">
      <meta property="twitter:description"
            :content=" currentProduct.Subtitle ?? currentProduct.Name">
      <meta property="twitter:image" :content="mediaElements.length > 0 &&
      mediaElements[currentMedia].type === 'audio' ?
      '/VTStack_partage_sound_assets.jpg':
      currentProduct.Previews > 0 ?
      currentProduct.Previews[0] : '/VTStack_partage_graph_assets.jpg'">
    </Head>
    <div class="product-content">
        <div class="product-elements">
          <TheBreadcrumb :currentCategory="currentCategory"
                         :currentProduct="currentProduct.Name" />
          <div class="product-elements-title mt-5">
            <h1 class="product-title">
              {{currentProduct.Name}}
            </h1>
          </div>
          <h2 v-if="currentProduct.Subtitle" class="product-subtitle  pb-5">
            {{currentProduct.Subtitle}}
          </h2>
          <div v-if="mediaElements.length > 0" class="w-100 pb-5 product-media-container">
            <div v-if="mediaElements[currentMedia].type === 'video'">
              <vue-plyr :options="videoOptions">
                <video
                  class="w-100"
                  :poster="require('@/assets/images/background/Bg_Video_Player.jpg')"
                  :src="mediaElements[currentMedia].src"
                  type="video/mp4" controls ></video>
              </vue-plyr>
            </div>
            <div v-else-if="mediaElements[currentMedia].type === 'audio'"
                 class="pt-3 align-self-center audio-player">
              <vue-plyr :options="videoOptions">
                <audio
                  class="w-100"
                  :src="mediaElements[currentMedia].src"
                  controls ></audio>
              </vue-plyr>
            </div>
            <div v-else-if="mediaElements[currentMedia].type === 'image'"
                 class="product-image">
              <img
                class="product-media"
                v-lazy="mediaElements[currentMedia].src" alt="preview"/>
            </div>
          </div>
          <div v-if="mediaElements.length > 1" class="d-flex w-100 flex-wrap">
            <img v-for="media in mediaElements"
                 v-lazy="media.thumb"
                 :key="media.id"
                 class="media-preview"
                 :class="{'active' : media.id === currentMedia }"
                 @click="changeMedia(media.id)"/>
          </div>
          <div v-if="currentProduct.Description" class="product-description mb-5"
               v-html="currentProduct.Description"/>
        </div>
        <div>
          <div class="product-action mt-7">
            <div class="button with-icon mb-5" @click="downloadAsset()">
              <div class="icon" >
                <img v-if="!downloadLoading" src="@/assets/images/icons/Icon_Btn_Download_White.svg"
                     :alt="currentProduct.Title + '_download_button'">
                <img v-else src="@/assets/images/loading/loading-spin.svg"
                     :alt="currentProduct.Title + '_wait_button'">
              </div>
              <span v-if="!downloaded()">
              {{ t('label.download') }}
              </span>
              <span v-if="downloaded()">
              {{ t('label.redownload') }}
              </span>
            </div>
            <label class="product-subtitle" v-html="downloadHelp">
            </label>
          </div>
        </div>
      </div>
  </div>
</template>

<script>
import store from '@/store';
import { Head } from '@vueuse/head';
import {
  CHECK_AUTH,
  DOWNLOAD_PRODUCT,
  FETCH_PRODUCT, FETCH_PRODUCTS,
  LOGIN,
} from '@/store/actions.type';
import { mapGetters } from 'vuex';
import { saveUrlReferer } from '@/common/referer.service';
import { APP_URL } from '@/common/config';
import TheBreadcrumb from '@/components/ui/TheBreadcrumb.vue';
import { useI18n } from 'vue-i18n';

export default {
  name: 'ProductView',
  setup() {
    const { t } = useI18n();
    return {
      t,
    };
  },
  components: {
    Head,
    TheBreadcrumb,
  },
  computed: {
    ...mapGetters(['currentUser', 'currentProduct', 'currentCategory']),
    mediaElements() {
      const elements = [];
      let id = 0;
      this.currentProduct.Videos.forEach((value) => {
        const element = {
          id,
          src: value,
          thumb: this.images('./Bg_Video_Player.jpg'),
          type: 'video',
        };
        id += 1;
        elements.push(element);
      });
      this.currentProduct.Previews.forEach((value) => {
        const element = {
          id,
          src: value,
          thumb: value,
          type: 'image',
        };
        id += 1;
        elements.push(element);
      });
      this.currentProduct.Audios.forEach((value) => {
        const element = {
          id,
          src: value,
          thumb: value,
          type: 'audio',
        };
        id += 1;
        elements.push(element);
      });
      return elements;
    },
    downloadHelp() {
      switch (this.currentCategory) {
        default:
        case 'graphs_assets': return this.$t('help.download.graph');
        case 'design_assets': return this.$t('help.download.design');
        case 'sound_assets': return this.$t('help.download.sound');
        case 'support_assets': return this.$t('help.download.support');
      }
    },
  },
  beforeRouteEnter(to, from, next) {
    store.dispatch(FETCH_PRODUCT, to.params.product)
      .then((found) => {
        if (found) {
          next();
        } else {
          next('');
        }
      });
  },
  beforeRouteUpdate(to, from, next) {
    const { locale } = this.$root.$i18n;
    store.dispatch(FETCH_PRODUCTS, { type: to.params.productCategory, locale })
      .then(() => {
        store.dispatch(FETCH_PRODUCT, to.params.product)
          .then((found) => {
            if (found) {
              next();
            } else {
              next('');
            }
          });
      });
  },
  mounted() {
    const ga = document.createElement('script');
    ga.setAttribute('src', 'https://www.googletagmanager.com/gtag/js?id=G-MFQVNJCY39');
    ga.setAttribute('async', '');
    document.head.appendChild(ga);
    /* eslint-disable */
    window.dataLayer = window.dataLayer || [];
    function gtag(){ dataLayer.push(arguments); }
    gtag('js', new Date());
    gtag('config', 'G-MFQVNJCY39');
    /* eslint-enable */
  },
  data() {
    return {
      videoOptions: {
        settings: [],
      },
      currentMedia: 0,
      downloadLoading: false,
      isDownloaded: false,
      images: require.context('@/assets/images/background/', false, /\.jpg$/),
      appUrl: APP_URL,
    };
  },
  methods: {
    goBack() {
      this.$router.push({
        name: 'ProductsView',
        params: {
          type: this.currentCategory,
        },
      });
    },
    changeMedia(id) {
      this.currentMedia = id;
    },
    downloaded() {
      return this.isDownloaded || this.currentProduct.Downloaded;
    },
    async downloadAsset() {
      const id = this.currentProduct.Id;
      const extension = this.currentProduct.Filename.split('.').pop();
      const fileName = `${this.currentProduct.Name}.${extension}`;
      const lang = this.$i18n.locale;
      store.dispatch(CHECK_AUTH)
        .then(() => {
          if (this.currentUser) {
            this.downloadLoading = true;
            store.dispatch(DOWNLOAD_PRODUCT, { id, fileName, lang }).then(() => {
              this.downloadLoading = false;
              this.isDownloaded = true;
            });
          } else {
            this.$parent.$parent.$refs.confirmDialogue.show().then((result) => {
              if (result) {
                saveUrlReferer(this.$route.path);
                store.dispatch(LOGIN);
              }
            });
          }
        });
    },
  },
  watch: {
    '$i18n.locale': function _(locale) {
      // todo enhance this ugly patch
      // eslint-disable-next-line prefer-template
      window.location.href = '/' + locale + '/'
        + this.currentCategory + '/' + this.currentProduct.Slug;
    },
  },
};
</script>
<style>
.plyr__volume{
  min-width: 150px;
  max-width: 150px;
}
:root {
  --plyr-color-main: var(--primary);
  --plyr-range-track-height: 15px;
  --plyr-range-thumb-height: 20px;
  --plyr-audio-control-color: white;
  --plyr-audio-progress-buffered-background: white;
  --plyr-audio-controls-background: transparent;
}
</style>
